import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { ArcProvider, useArc } from './context/ArcContext';
import { AuthProvider, useAuth } from './context/AuthContext';

import NewLanding from './pages/NewLanding';
import ModernLanding from './pages/ModernLanding';
import EvolvingLanding from './pages/EvolvingLanding';
import MyarcNetworkPreview from './pages/MyarcNetworkPreview';
import Assessment from './pages/Assessment';
import Results from './pages/Results';
import WaitlistThankYou from './pages/WaitlistThankYou';
import { FounderApplication, ConstellationWaitlist, IntakeReceived } from './pages/ConstellationIntake';
import { PrivacyPage, TermsPage } from './pages/LegalPages';
import SharedArc from './pages/SharedArc';
import Login from './pages/Login';
import Baseline from './pages/Baseline';

import AppLayout from './components/AppLayout';
import Dashboard from './pages/Dashboard';
import ProtocolGrid from './pages/ProtocolGrid';
import LifeGoals from './pages/LifeGoals';
import StrategyStudio from './pages/StrategyStudio';
import Financials from './pages/Financials';
import Modules from './pages/Modules';
import Profile from './pages/Profile';
import Network from './pages/Network';
import MyArc from './pages/MyArc';

import { Toaster } from './components/ui/sonner';

function EngineVarsInjector({ children }) {
  const { engine } = useArc();

  useEffect(() => {
    const root = document.documentElement;
    if (engine) {
      root.style.setProperty('--el', engine.light);
      root.style.setProperty('--es', engine.shadow);
      root.style.setProperty('--ep', engine.primary);
    } else {
      root.style.setProperty('--el', '#C7A46D');
      root.style.setProperty('--es', '#5c3d1d');
      root.style.setProperty('--ep', '#B5925F');
    }
  }, [engine]);

  return children;
}

function LoadingScreen({ label = 'Loading...' }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B0A08',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#C7A46D',
      fontSize: 14,
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      fontFamily: 'var(--font-label)',
    }}>
      {label}
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function RequireLifeOSAccess({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user?.life_os_access) return <Navigate to="/waitlist" replace state={{ lifeOsPrivate: true, email: user?.email }} />;
  return children;
}

function hasAssignedArc(state) {
  return Boolean(state.arcAssigned && state.arcKey && state.engineKey);
}

const LIFE_OS_ARCHIVED = true;

function RequireArcAssigned({ children }) {
  const { state, hydrated } = useArc();

  if (!hydrated) return <LoadingScreen label="Loading Life OS..." />;
  if (!hasAssignedArc(state)) return <Navigate to="/assessment" replace state={{ reason: 'arc_required' }} />;
  return children;
}

function LifeOSReadyGate({ children }) {
  const { state, hydrated } = useArc();
  const location = useLocation();

  if (!hydrated) return <LoadingScreen label="Loading Life OS..." />;
  if (!hasAssignedArc(state)) return <Navigate to="/assessment" replace state={{ reason: 'arc_required' }} />;
  const baselineIsStale = state.assessmentTaken && state.baselineAssessmentTaken !== state.assessmentTaken;
  if (!state.baselineRituals || baselineIsStale) {
    return <Navigate to="/app/baseline" replace state={{ from: location.pathname }} />;
  }

  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ArcProvider>
          <EngineVarsInjector>
            <BrowserRouter>
              <Routes>
                {/* Public root: ARC Constellation landing (was at /v3) */}
                <Route path="/" element={<EvolvingLanding />} />
                {/* /v3 kept as alias so deep links keep working */}
                <Route path="/v3" element={<EvolvingLanding />} />
                {/* Legacy landings, kept under explicit paths */}
                <Route path="/legacy" element={<NewLanding />} />
                <Route path="/modern" element={<ModernLanding />} />
                <Route path="/v2" element={<ModernLanding />} />
                <Route path="/myarc-network-preview" element={<MyarcNetworkPreview />} />
                <Route path="/myarc-constellation-preview" element={<MyarcNetworkPreview />} />
                <Route path="/login" element={<Login />} />
                <Route path="/apply" element={<FounderApplication />} />
                <Route path="/join" element={<ConstellationWaitlist />} />
                <Route path="/application-received" element={<IntakeReceived />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/arc-signal" element={<Assessment />} />
                <Route path="/assessment" element={<Navigate to="/apply" replace />} />
                <Route path="/results" element={<Results />} />
                <Route path="/waitlist" element={<WaitlistThankYou />} />
                <Route path="/a/:token" element={<SharedArc />} />
                {LIFE_OS_ARCHIVED ? (
                  <>
                    <Route path="/app/baseline" element={<Navigate to="/waitlist" replace state={{ lifeOsArchived: true }} />} />
                    <Route path="/app/*" element={<Navigate to="/waitlist" replace state={{ lifeOsArchived: true }} />} />
                  </>
                ) : (
                  <>
                    <Route path="/app/baseline" element={<RequireAuth><RequireLifeOSAccess><RequireArcAssigned><Baseline /></RequireArcAssigned></RequireLifeOSAccess></RequireAuth>} />

                    <Route path="/app" element={<RequireAuth><RequireLifeOSAccess><LifeOSReadyGate><AppLayout /></LifeOSReadyGate></RequireLifeOSAccess></RequireAuth>}>
                      <Route index element={<Dashboard />} />
                      <Route path="protocol" element={<ProtocolGrid />} />
                      <Route path="goals" element={<LifeGoals />} />
                      <Route path="strategy" element={<StrategyStudio />} />
                      <Route path="financials" element={<Financials />} />
                      <Route path="modules" element={<Modules />} />
                      <Route path="network" element={<Network />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="myarc" element={<MyArc />} />
                    </Route>
                  </>
                )}

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster />
            </BrowserRouter>
          </EngineVarsInjector>
        </ArcProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
